/**
 * Moya product sticky bar — keeps quantity in sync between the sticky
 * add-to-cart bar's own <quantity-selector> and the main one on the page.
 * Loaded via sections/main-product.liquid, only when the sticky bar is
 * enabled.
 *
 * QuantitySelector/QuantityInput (theme.js) are self-contained custom
 * elements with no built-in cross-instance sync. Two independent
 * <quantity-selector> bound to the same form via the input's `form`
 * attribute would otherwise drift out of sync.
 *
 * The sticky selector is rendered with `submit: false` (no name) so it
 * never participates in FormData. Only the main `name="quantity"` is
 * posted. That matters because ProductForm sends `new FormData(form)`
 * and Shopify's cart/add.js keeps the last duplicate `quantity` — the
 * sticky input sits later in the DOM and would otherwise overwrite the
 * PDP value with its default of 1.
 *
 * QuantitySelector +/- dispatches `change` (not `input`) after stepUp /
 * stepDown, so both events must be mirrored.
 *
 * Also: product-sticky-bar's <product-rerender> replaces its whole
 * subtree (including its quantity-selector) whenever the variant changes,
 * so newly-inserted inputs need their listeners (re)bound and their
 * value copied from the submitted input — the MutationObserver below
 * handles that.
 */
function peersFor(input) {
	const formId = input.getAttribute("form");

	if (!formId) {
		return [];
	}

	return Array.from(document.querySelectorAll(`quantity-selector input[form="${CSS.escape(formId)}"]`));
}

function applyQuantity(input, value) {
	if (!input.isConnected || input.value === value) {
		return;
	}

	const selector = input.closest("quantity-selector");

	if (selector) {
		selector.quantity = value;
	} else {
		input.value = value;
	}
}

function syncPeers(input) {
	peersFor(input).forEach((other) => {
		if (other !== input) {
			applyQuantity(other, input.value);
		}
	});
}

function alignGroup(group) {
	const source = group.find((input) => input.isConnected && input.name === "quantity") || group.find((input) => input.isConnected);

	if (!source) {
		return;
	}

	group.forEach((other) => {
		if (other !== source) {
			applyQuantity(other, source.value);
		}
	});
}

function bindQuantitySync(input) {
	if (input.dataset.quantitySyncBound) {
		return;
	}

	input.dataset.quantitySyncBound = "true";

	input.addEventListener("input", () => syncPeers(input));
	input.addEventListener("change", () => syncPeers(input));
}

function syncQuantityInputs() {
	const byForm = new Map();

	document.querySelectorAll("quantity-selector input[form]").forEach((input) => {
		const formId = input.getAttribute("form");

		if (!byForm.has(formId)) {
			byForm.set(formId, []);
		}

		byForm.get(formId).push(input);
	});

	byForm.forEach((group) => {
		if (group.length < 2) {
			return;
		}

		group.forEach((input) => bindQuantitySync(input));
		alignGroup(group);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", syncQuantityInputs);
} else {
	syncQuantityInputs();
}

const stickyBarObserver = new MutationObserver(() => {
	syncQuantityInputs();
});

stickyBarObserver.observe(document.body, { childList: true, subtree: true });
